import { Injectable } from "@angular/core";
import { ConfigService } from "../config.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { CustomerDonation, NetsuiteProgram } from ".";
import { DataTransform, DateField } from "./dataTransformFunctions";
import { of, Subject, switchMap, tap } from "rxjs";
import { CustomerDonationReview, DonationReviewWithLogs, RecurringDonation } from "./types/finance";


export type DonationUpdatedArgs = DonationCreatedOrUpdatedArgs | DonationDeletedArgs

export interface DonationCreatedOrUpdatedArgs {
    type: 'updated' | 'created'
    donation: CustomerDonation
}

export interface DonationDeletedArgs {
    type: 'deleted'
    id: number
}
@Injectable({
    providedIn: 'root'
})
export class CustomerDonationService {

    private donationUpdatedSubject = new Subject<DonationUpdatedArgs>()
    readonly donationUpdated$ = this.donationUpdatedSubject.asObservable()

    constructor(
        private http: HttpClient,
        private config: ConfigService,

    ) { }

    readonly customerDonationsDateFields: DateField<CustomerDonation>[] = [
        { fieldName: 'donationDate', dateType: "utc" },
        { fieldName: 'createdDate', dateType: "utc" }
    ]

    readonly recurringDonationsDateFields: DateField<RecurringDonation>[] = [
        { fieldName: 'billingCycleAnchor', dateType: "utc" }
    ]

    getCustomerDonations(customerId: number, options?: { includeChildren?: boolean }) {

        const params = new HttpParams().set('includeChildren', options?.includeChildren ? 'true' : 'false')

        return this.http.get<CustomerDonation[]>(`${this.config.dataApiUrl}/customer-donations/customer/${customerId.toString()}`, { params }).pipe(
            switchMap(donations =>
                DataTransform.replaceDatesArray<CustomerDonation>(donations, this.customerDonationsDateFields)
            )
        )
    }

    createCustomerDonation(customerDonation: Omit<CustomerDonation, 'id' | 'createdDate' | 'stripeLogId' | 'paypalLogId'>) {
        return this.http.post<CustomerDonation>(`${this.config.dataApiUrl}/customer-donations`, customerDonation)
            .pipe(
                switchMap(donation =>
                    DataTransform.replaceDates<CustomerDonation>(donation, this.customerDonationsDateFields)
                ),
                tap(donation => this.donationUpdatedSubject.next({ type: 'created', donation }))
            )
    }

    updateCustomerDonation(id: number, customerDonation: Omit<CustomerDonation, 'id' | 'createdDate' | 'stripeLogId'>) {
        return this.http.put<CustomerDonation>(`${this.config.dataApiUrl}/customer-donations/${id}`, customerDonation)
            .pipe(
                switchMap(donation =>
                    DataTransform.replaceDates<CustomerDonation>(donation, this.customerDonationsDateFields)
                ),
                tap(donation => this.donationUpdatedSubject.next({ type: 'updated', donation }))
            )
    }

    /**
     * Gets all netsuite programs (donation funds).  Server adds a 30 minute cache header, so this is safe to call frequenty.
     * @returns 
     */
    getFunds(listType: "active" | "all" = "all") {
        return this.http.get<NetsuiteProgram[]>(`${this.config.dataApiUrl}/customer-donations/funds`).pipe(
            switchMap(programs => {
                if (listType === "active") {
                    return of(programs.filter(p => p.active))
                }
                else {
                    return of(programs)
                }
            })
        )
    }

    getRecurringDonationsForCustomer(customerId: number) {

        return this.http.get<RecurringDonation[]>(`${this.config.dataApiUrl}/customer-donations/customer/${customerId}/recurring`).pipe(
            switchMap(donations => {
                return DataTransform.replaceDatesArray<RecurringDonation>(donations, this.recurringDonationsDateFields)
            })
        )
    }

    getDonationReviewQueue() {
        return this.http.get<DonationReviewWithLogs[]>(`${this.config.dataApiUrl}/customer-donations/review-queue`).pipe(
            switchMap(reviews => {
                return DataTransform.replaceDatesArray<DonationReviewWithLogs>(reviews, [{fieldName: 'transactionDate', dateType: 'utc'}])
            })
        )
    }

}